import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore'
import { db } from '../lib/firebase'

interface ReservationData {
  raffleId: string
  numbers: number[]
  sessionId: string
  reservationOwnerId: string
  expiresAt: Date
}

export interface ConflictCheck {
  hasConflicts: boolean
  conflictingNumbers: number[]
  availableNumbers: number[]
}

class ReservationService {
  private readonly RESERVATION_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Check for conflicts before reserving numbers
   * @param raffleId - The raffle ID
   * @param numbers - Numbers to check
   * @param currentSessionId - Current user's session ID (to exclude from conflicts)
   */
  async checkForConflicts(raffleId: string, numbers: number[], currentSessionId?: string): Promise<ConflictCheck> {
    try {
      const now = new Date()
      
      // Query for any tickets that are either:
      // 1. Already purchased (have userId)
      // 2. Reserved and not expired
      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('raffleId', '==', raffleId),
        where('number', 'in', numbers)
      )

      const querySnapshot = await getDocs(ticketsQuery)
      const conflictingNumbers: number[] = []

      // Group tickets by number to handle duplicates
      const ticketsByNumber = new Map<number, any[]>()

      querySnapshot.forEach((doc) => {
        const ticket = doc.data()
        const number = ticket.number

        if (!ticketsByNumber.has(number)) {
          ticketsByNumber.set(number, [])
        }
        ticketsByNumber.get(number)!.push(ticket)
      })

      // Check each number for conflicts
      ticketsByNumber.forEach((tickets, number) => {
        console.log(`🔍 Verificando número ${number} - ${tickets.length} ticket(s) encontrado(s)`)

        // PRIORITY 1: Check if ANY ticket is sold (highest priority)
        const soldTicket = tickets.find(t => t.userId && t.purchasedAt)
        if (soldTicket) {
          console.log(`🔴 Número ${number}: VENDIDO (userId: ${soldTicket.userId})`)
          conflictingNumbers.push(number)
          return
        }

        // PRIORITY 2: Check if ANY active (non-expired) reservation exists
        const activeReservation = tickets.find(t => {
          if (t.sessionId && t.reservationExpiresAt) {
            const expiresAt = t.reservationExpiresAt.toDate()
            const isExpired = now >= expiresAt

            console.log(`  📋 Ticket con sessionId ${t.sessionId?.substring(0, 8)}...`, {
              expiresAt: expiresAt.toISOString(),
              now: now.toISOString(),
              isExpired,
              isMine: t.sessionId === currentSessionId
            })

            return !isExpired
          }
          return false
        })

        if (activeReservation) {
          // Active reservation found - check ownership
          if (activeReservation.sessionId !== currentSessionId) {
            console.log(`❌ Número ${number}: CONFLICTO (reservado por otro usuario y no expirado)`)
            conflictingNumbers.push(number)
          } else {
            console.log(`✅ Número ${number}: YA ES TUYO (no expirado)`)
          }
        } else {
          console.log(`✅ Número ${number}: LIBRE (sin reservas activas ni ventas)`)
        }
      })

      const availableNumbers = numbers.filter(n => !conflictingNumbers.includes(n))
      

      return {
        hasConflicts: conflictingNumbers.length > 0,
        conflictingNumbers,
        availableNumbers
      }
    } catch (error) {
      console.error('Error checking for conflicts:', error)
      throw new Error('Error al verificar disponibilidad de números')
    }
  }

  /**
   * Reserve numbers for a session
   * CRITICAL FIX: Uses writeBatch for atomic writes and re-validates before committing
   * Note: Full TOCTOU prevention requires Firestore Security Rules - this minimizes the race window
   */
  async reserveNumbers(data: ReservationData): Promise<boolean> {
    try {
      // First conflict check
      const conflictCheck = await this.checkForConflicts(data.raffleId, data.numbers, data.sessionId)

      if (conflictCheck.hasConflicts) {
        throw new Error(`Los números ${conflictCheck.conflictingNumbers.join(', ')} ya no están disponibles`)
      }

      // Prepare batch operation for atomicity
      const batch = writeBatch(db)
      const ticketsToCreate: { number: number; docRef: any }[] = []
      const ticketsToUpdate: { number: number; docRef: any }[] = []

      // Gather all existing tickets and prepare batch operations
      for (const number of data.numbers) {
        const existingTicketQuery = query(
          collection(db, 'tickets'),
          where('raffleId', '==', data.raffleId),
          where('number', '==', number)
        )

        const existingSnapshot = await getDocs(existingTicketQuery)

        const reservationDoc = {
          raffleId: data.raffleId,
          number,
          sessionId: data.sessionId,
          reservationOwnerId: data.reservationOwnerId,
          userId: null,
          orderId: null,
          isReserved: true,
          reservedAt: serverTimestamp(),
          reservationExpiresAt: Timestamp.fromDate(data.expiresAt),
          purchasePrice: 0,
          purchasedAt: null
        }

        if (!existingSnapshot.empty) {
          // Find expired reservation to update
          const expiredReservation = existingSnapshot.docs.find(d => {
            const ticket = d.data()
            return ticket.isReserved && !ticket.purchasedAt && !ticket.userId
          })

          if (expiredReservation) {
            console.log(`♻️ Actualizando ticket existente para número ${number}`)
            batch.update(doc(db, 'tickets', expiredReservation.id), reservationDoc)
            ticketsToUpdate.push({ number, docRef: expiredReservation.id })
          } else {
            console.warn(`⚠️ Número ${number}: vendido, no se puede reservar`)
            throw new Error(`Número ${number} no disponible para reserva`)
          }
        } else {
          const newDocRef = doc(collection(db, 'tickets'))
          console.log(`✨ Creando nuevo ticket para número ${number}`)
          batch.set(newDocRef, reservationDoc)
          ticketsToCreate.push({ number, docRef: newDocRef.id })
        }
      }

      // Re-validate right before committing to minimize TOCTOU window
      const reCheck = await this.checkForConflicts(data.raffleId, data.numbers, data.sessionId)
      if (reCheck.hasConflicts) {
        console.warn('⚠️ Race condition detected - conflicts appeared during batch preparation')
        throw new Error(`Los números ${reCheck.conflictingNumbers.join(', ')} ya no están disponibles`)
      }

      // Atomic commit
      await batch.commit()
      console.log(`✅ Batch committed: ${ticketsToCreate.length} created, ${ticketsToUpdate.length} updated`)

      return true
    } catch (error) {
      console.error('Error reserving numbers:', error)
      throw error
    }
  }

  /**
   * Release reservations for a session
   */
  async releaseReservation(sessionId: string): Promise<void> {
    try {
      const reservationsQuery = query(
        collection(db, 'tickets'),
        where('sessionId', '==', sessionId),
        where('isReserved', '==', true)
      )

      const querySnapshot = await getDocs(reservationsQuery)
      const deletePromises: Promise<void>[] = []

      querySnapshot.forEach((docSnapshot) => {
        const ticket = docSnapshot.data()
        // Only delete if not purchased (no userId or purchasedAt)
        if (!ticket.userId || !ticket.purchasedAt) {
          deletePromises.push(deleteDoc(doc(db, 'tickets', docSnapshot.id)))
        } else {
          // Already purchased, keep the record
        }
      })

      await Promise.all(deletePromises)
    } catch (error) {
      console.error('Error releasing reservation:', error)
      throw new Error('Error al liberar la reserva')
    }
  }

  /**
   * Extend reservation time
   */
  async extendReservation(sessionId: string, additionalMinutes: number): Promise<void> {
    try {
      const newExpirationTime = new Date(Date.now() + (additionalMinutes * 60 * 1000))
      
      const reservationsQuery = query(
        collection(db, 'tickets'),
        where('sessionId', '==', sessionId),
        where('isReserved', '==', true)
      )

      const querySnapshot = await getDocs(reservationsQuery)
      const updatePromises: Promise<void>[] = []

      querySnapshot.forEach((docSnapshot) => {
        updatePromises.push(
          updateDoc(doc(db, 'tickets', docSnapshot.id), {
            reservationExpiresAt: Timestamp.fromDate(newExpirationTime)
          })
        )
      })

      await Promise.all(updatePromises)
    } catch (error) {
      console.error('Error extending reservation:', error)
      throw new Error('Error al extender la reserva')
    }
  }

  /**
   * Convert reservation to purchase
   */
  async confirmPurchase(sessionId: string, userId: string, purchasePrice: number): Promise<void> {
    try {
      const reservationsQuery = query(
        collection(db, 'tickets'),
        where('sessionId', '==', sessionId),
        where('isReserved', '==', true)
      )

      const querySnapshot = await getDocs(reservationsQuery)
      const updatePromises: Promise<void>[] = []

      querySnapshot.forEach((docSnapshot) => {
        updatePromises.push(
          updateDoc(doc(db, 'tickets', docSnapshot.id), {
            userId,
            purchasePrice,
            purchasedAt: serverTimestamp(),
            isReserved: false,
            // Keep sessionId for tracking purposes
          })
        )
      })

      await Promise.all(updatePromises)
    } catch (error) {
      console.error('Error confirming purchase:', error)
      throw new Error('Error al confirmar la compra')
    }
  }

  /**
   * Clean up expired reservations (should be called periodically)
   */
  async cleanupExpiredReservations(): Promise<void> {
    try {
      const now = new Date()
      
      const expiredQuery = query(
        collection(db, 'tickets'),
        where('isReserved', '==', true),
        where('reservationExpiresAt', '<', Timestamp.fromDate(now))
      )

      const querySnapshot = await getDocs(expiredQuery)
      const deletePromises: Promise<void>[] = []

      querySnapshot.forEach((docSnapshot) => {
        const ticket = docSnapshot.data()
        
        // Only delete if not purchased
        if (!ticket.userId || !ticket.purchasedAt) {
          deletePromises.push(deleteDoc(doc(db, 'tickets', docSnapshot.id)))
        }
      })

      if (deletePromises.length > 0) {
        await Promise.all(deletePromises)
      }
    } catch (error) {
      console.error('Error cleaning up expired reservations:', error)
    }
  }

  /**
   * Get reservation status for numbers
   */
  async getReservationStatus(raffleId: string, numbers: number[]): Promise<{ [key: number]: 'available' | 'reserved' | 'sold' }> {
    try {
      const now = new Date()
      const status: { [key: number]: 'available' | 'reserved' | 'sold' } = {}
      
      // Initialize all as available
      numbers.forEach(num => {
        status[num] = 'available'
      })

      const ticketsQuery = query(
        collection(db, 'tickets'),
        where('raffleId', '==', raffleId),
        where('number', 'in', numbers)
      )

      const querySnapshot = await getDocs(ticketsQuery)

      querySnapshot.forEach((doc) => {
        const ticket = doc.data()
        const number = ticket.number

        // Check if sold
        if (ticket.userId && ticket.purchasedAt) {
          status[number] = 'sold'
          return
        }

        // Check if reserved and not expired
        if (ticket.sessionId && ticket.reservationExpiresAt) {
          const expiresAt = ticket.reservationExpiresAt.toDate()
          // Use > to check if not expired (expired when now >= expiresAt)
          if (now < expiresAt) {
            status[number] = 'reserved'
          }
        }
      })

      return status
    } catch (error) {
      console.error('Error getting reservation status:', error)
      throw new Error('Error al obtener el estado de reservas')
    }
  }
}

export const reservationService = new ReservationService()
export type { ReservationData }
